import { Component, Input } from '@angular/core';

import { AppService } from '../../../services/app.service';

import { ITableColumn } from '../../../shared/table/table.component';
import { HospitalService } from '../../../services/hospital.service';
import { IHospitalStudent } from '../../../models/Hospital';
import { store } from '../store';

interface IOption<T> {
    value: T;
    label: string;
}

interface ICheckOption<T> extends IOption<T> {
    checked?: boolean;
}

type Place = 'workplaceAndClinicalBase' | 'workplace' | 'clinicalBase';

const statuses = {
    active: 1,
    vacation: 2,
    exchange: 7
};

const cache: {
    studentsByCompany: { [companyId: string]: IHospitalStudent[] }
} = {
    studentsByCompany: {}
};

@Component({
    selector: 'app-hospital-dashboard-students',
    templateUrl: './hospital-dashboard-students.component.html'
})
export class HospitalDashboardStudentsComponent {
    constructor(
        private app: AppService,
        private service: HospitalService
    ) { }

    @Input() set companyId(value: string) {
        this._companyId = value;
        this.init(true);
    }

    @Input() set academicYearId(value: string) {
        this._academicYearId = value;
        this.init();
    }

    readonly columns: ITableColumn[] = [
        {
            label: 'hospitalDashboard_students_lblNameSurnameId', sorts: true, sortFn: (a: IHospitalStudent, b: IHospitalStudent) => {
                const as = `${a.Name} ${a.Surname} ${a.Upn}`;
                const bs = `${b.Name} ${b.Surname} ${b.Upn}`;
                return as.localeCompare(bs, this.app.currentLanguage);
            }
        },
        { property: 'Speciality', label: 'hospitalDashboard_students_lblSpeciality', sorts: true },
        { property: 'StudyYear', label: 'hospitalDashboard_students_lblStudyYear', sorts: true, type: 'number' },
        { property: 'Status', label: 'hospitalDashboard_students_lblStatus', sorts: true },
        { property: 'Workplace', label: 'hospitalDashboard_students_lblWorkplace', sorts: true },
        { property: 'ClinicalBase', label: 'hospitalDashboard_students_lblClinicalBase', sorts: true },
        { width: '1px' }
    ];

    rows: IHospitalStudent[] = [];

    readonly filters: {
        search?: string,
        speciality: {
            options: IOption<string>[],
            value?: string
        },
        year: {
            options: IOption<number>[],
            value?: number
        },
        status: {
            options: IOption<number>[],
            value: number[]
        },
        place: {
            options: ICheckOption<Place>[]
        },
        workplace: {
            options: IOption<string>[],
            value?: string
        },
        clinicalBase: {
            options: IOption<string>[],
            value?: string
        },
        plannedRotations: ICheckOption<boolean>,
        guest: ICheckOption<boolean>
    } = {
            speciality: { options: [] },
            year: { options: [] },
            status: { options: [], value: [statuses.active, statuses.vacation, statuses.exchange] },
            place: {
                options: [
                    { value: 'workplace', label: 'hospitalDashboard_students_workplace' },
                    { value: 'clinicalBase', label: 'hospitalDashboard_students_clinicalBase' }
                ]
            },
            workplace: { options: [] },
            clinicalBase: { options: [] },
            plannedRotations: { value: false, label: 'hospitalDashboard_students_plannedRotations' },
            guest: { value: false, label: 'hospitalDashboard_students_guest' }
        };

    get companyId(): string {
        return this._companyId;
    }

    get academicYearId(): string {
        return this._academicYearId;
    }

    private _companyId: string;
    private _academicYearId: string;
    private data: IHospitalStudent[] = [];

    filter() {
        const places = this.filters.place.options.filter(t => t.checked).map(t => t.value);

        this.rows = this.data.filter(t => {
            return (!this.filters.speciality.value || t.SpecialityId == this.filters.speciality.value)
                && (!this.filters.year.value || t.StudyYear == this.filters.year.value)
                && (!this.filters.status.value.length || this.filters.status.value.includes(t.StatusId))
                && (!this.filters.workplace.value || t.WorkplaceId == this.filters.workplace.value)
                && (!this.filters.clinicalBase.value || t.ClinicalBaseId == this.filters.clinicalBase.value)
                && (this.isPlaceMatch(t, places))
                && (!this.filters.plannedRotations.checked || t.RotationBaseId)
                && (!this.filters.guest.checked || (t.ClinicalBaseId != this.companyId && t.WorkplaceId != this.companyId))
                && (!this.filters.search || (t.Name + ' ' + t.Surname).toLowerCase().includes(this.filters.search.toLowerCase()));
        });
    }

    getPlanSummaryUrl(row: IHospitalStudent): string {
        return `/${this.app.currentLanguage}/plan-summary?StudentId=${row.Id}&ProgramId=${row.SpecialityId}`;
    }

    private init(reload?: boolean) {
        const process = (data: IHospitalStudent[]) => {
            const byYear = this.filterByAcademicYear(data);

            this.addFilters(byYear);
            this.data = byYear;
            this.filter();
        };

        const cached = store.getStudents(this.companyId);

        if (cached) {
            process(cached);
        } else if (reload) {
            this.app.addLoading(store.loadStudents(this.service, this.companyId, true)).subscribe(data => {
                process(data);
            });
        }
    }

    private filterByAcademicYear(data: IHospitalStudent[]) {
        return data.filter(t => t.AcademicYearId == this.academicYearId);
    }

    private addFilters(data: IHospitalStudent[]) {
        const specialityOpts: IOption<string>[] = [];
        const yearOpts: IOption<number>[] = [];
        const statusOpts: IOption<number>[] = [];
        const workplaceOpts: IOption<string>[] = [];
        const clinicalBaseOpts: IOption<string>[] = [];

        data.forEach(n => {
            if (n.Speciality && !specialityOpts.some(t => t.value == n.SpecialityId)) {
                specialityOpts.push({
                    value: n.SpecialityId,
                    label: n.Speciality
                });
            }

            if (n.StudyYear && !yearOpts.some(t => t.value == n.StudyYear)) {
                yearOpts.push({
                    value: n.StudyYear,
                    label: `${n.StudyYear}.`
                });
            }

            if (n.Status && !statusOpts.some(t => t.value == n.StatusId)) {
                statusOpts.push({
                    value: n.StatusId,
                    label: n.Status
                });
            }

            if (n.Workplace && !workplaceOpts.some(t => t.value == n.WorkplaceId)) {
                workplaceOpts.push({
                    value: n.WorkplaceId,
                    label: n.Workplace
                });
            }

            if (n.ClinicalBase && !clinicalBaseOpts.some(t => t.value == n.ClinicalBaseId)) {
                clinicalBaseOpts.push({
                    value: n.ClinicalBaseId,
                    label: n.ClinicalBase
                });
            }
        });

        specialityOpts.sort((a, b) => a.label.localeCompare(b.label, this.app.currentLanguage));
        yearOpts.sort((a, b) => a.value > b.value ? 1 : a.value < b.value ? -1 : 0);
        statusOpts.sort((a, b) => a.label.localeCompare(b.label, this.app.currentLanguage));
        workplaceOpts.sort((a, b) => a.label.localeCompare(b.label, this.app.currentLanguage));
        clinicalBaseOpts.sort((a, b) => a.label.localeCompare(b.label, this.app.currentLanguage));

        this.filters.speciality.options = specialityOpts;
        this.filters.year.options = yearOpts;
        this.filters.status.options = statusOpts;
        this.filters.workplace.options = workplaceOpts;
        this.filters.clinicalBase.options = clinicalBaseOpts;
    }

    private isPlaceMatch(student: IHospitalStudent, places: Place[]) {
        if (places.includes('workplace') && student.WorkplaceId != this.companyId) return false;
        if (places.includes('clinicalBase') && student.ClinicalBaseId != this.companyId) return false;
        return true;
    }
}
